import React, { useState, useEffect, useMemo } from 'react';
import managerService from '../services/managerService'; // Import the new service
import './AmanageBroker.css'; // Import your CSS styles

// --- BrokerModal Component (Slightly Modified) ---
const BrokerModal = ({ isOpen, onClose, onSave, isSubmitting }) => {
  // Note: We only support ADDING brokers now, as there's no backend endpoint for general editing.
  const [formData, setFormData] = useState({ name: '', email: '', password: '', phoneNumber: '', licenseNumber: '' });

  useEffect(() => {
    // Reset form when modal opens
    if (isOpen) {
      setFormData({ name: '', email: '', password: 'abc123', phoneNumber:  '' , licenseNumber: '' });
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData);
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={e => e.stopPropagation()}>
        <form onSubmit={handleSubmit}>
          <h2>Add New Broker</h2>
          <div className="form-group">
            <label>Full Name</label>
            <input type="text" name="name" value={formData.name} onChange={handleChange} required />
          </div>
          <div className="form-group">
            <label>Email Address</label>
            <input type="email" name="email" value={formData.email} onChange={handleChange} required />
          </div>
          <div className="form-group">
            <label>Initial Password</label>
            <input
              type="text"
              name="password"
              value="abc123"
              readOnly // This makes the field un-editable
              placeholder='abc123'
              style={{ backgroundColor: '#f0f0f0' }} // Optional: Style it to look disabled
            />          </div>

          <div className="form-group">
            <label>Phone Number</label>
            <input type="tel" name="phoneNumber" value={formData.phoneNumber} onChange={handleChange} />
          </div>
          <div className="form-group">
            <label>License Number</label>
            <input type="text" name="licenseNumber" value={formData.licenseNumber} onChange={handleChange} />
          </div>

          <div className="modal-actions">
            <button type="button" className="btn-cancel" onClick={onClose} disabled={isSubmitting}>Cancel</button>
            <button type="submit" className="btn-save" disabled={isSubmitting}>
              {isSubmitting ? 'Creating...' : 'Create Broker'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};


const AmanageBroker = () => {
      const [allBrokers, setAllBrokers] = useState([]);
  const [filteredBrokers, setFilteredBrokers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // UI State
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('All');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Fetch initial data
  useEffect(() => {
    const loadBrokers = async () => {
      setLoading(true);
      try {
        const data = await managerService.getBrokers();
        // The backend `getUser` returns ALL users for an admin. Filter for brokers here.
        const brokersOnly = data.filter(user => user.role === 'broker');
        setAllBrokers(brokersOnly);
        setFilteredBrokers(brokersOnly);
      } catch (err) {
        setError('Failed to load brokers. Please try again later.');
      } finally {
        setLoading(false);
      }
    };
    loadBrokers();
  }, []);

  // Handle filtering and searching
  useEffect(() => {
    let result = allBrokers;

    // Filter by status
    if (statusFilter !== 'All') {
      const isActive = statusFilter === 'Active';
      result = result.filter(broker => broker.isActive === isActive);
    }

    // Filter by search term
    if (searchTerm) {
      const lowercasedTerm = searchTerm.toLowerCase();
      result = result.filter(broker =>
        broker.name.toLowerCase().includes(lowercasedTerm) ||
        broker.email.toLowerCase().includes(lowercasedTerm)
      );
    }

    setFilteredBrokers(result);
  }, [searchTerm, statusFilter, allBrokers]);

  // --- API Handlers ---
  const handleAddBroker = async (brokerData) => {
    setIsSubmitting(true);
    try {
      await managerService.addBroker(brokerData);
      // Refresh data after adding
      const data = await managerService.getBrokers();
      const brokersOnly = data.filter(user => user.role === 'broker');
      setAllBrokers(brokersOnly);
      setIsModalOpen(false);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to add broker.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleStatusToggle = async (brokerId, currentStatus) => {
    // Optimistic UI update
    const originalBrokers = [...allBrokers];
    const updatedBrokers = allBrokers.map(b =>
      b._id === brokerId ? { ...b, isActive: !currentStatus } : b
    );
    setAllBrokers(updatedBrokers);

    try {
      await managerService.toggleBrokerStatus(brokerId, !currentStatus);
    } catch (err) {
      setError('Failed to update status. Reverting change.');
      setAllBrokers(originalBrokers); // Revert on error
    }
  };

  // Memoized analysis data to avoid re-calculating on every render
  const analysisData = useMemo(() => {
    const total = allBrokers.length;
    const active = allBrokers.filter(b => b.isActive).length;
    return {
      total,
      active,
      inactive: total - active,
    };
  }, [allBrokers]);

  return (
    <div className="manage-broker-containers">
      <header className="page-header">
        <h1>Manage Brokers</h1>
        <button className="btn-add" onClick={() => setIsModalOpen(true)}>+ Add New Broker</button>
      </header>

      {error && <div className="error-banner">{error}</div>}

      {/* --- Analysis Cards --- */}
      <section className="analysis-cards">
        <div className="card">
          <div className="card-icon"><i className="bi bi-people-fill"></i></div>
          <div className="card-content">
            <span className="card-value">{analysisData.total}</span>
            <span className="card-label">Total Brokers</span>
          </div>
        </div>
        <div className="card">
          <div className="card-icon active"><i className="bi bi-check-circle-fill"></i></div>
          <div className="card-content">
            <span className="card-value">{analysisData.active}</span>
            <span className="card-label">Active</span>
          </div>
        </div>
        <div className="card">
          <div className="card-icon inactive"><i className="bi bi-x-circle-fill"></i></div>
          <div className="card-content">
            <span className="card-value">{analysisData.inactive}</span>
            <span className="card-label">Inactive</span>
          </div>
        </div>
      </section>

      {/* --- Table Card with Toolbar --- */}
      <div className="broker-table-card">
        <div className="toolbar">
          <input
            type="text"
            placeholder="Search by name or email..."
            className="search-input"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          <div className="filter-buttons">
            <button className={statusFilter === 'All' ? 'active' : ''} onClick={() => setStatusFilter('All')}>All</button>
            <button className={statusFilter === 'Active' ? 'active' : ''} onClick={() => setStatusFilter('Active')}>Active</button>
            <button className={statusFilter === 'Inactive' ? 'active' : ''} onClick={() => setStatusFilter('Inactive')}>Inactive</button>
          </div>
        </div>

        <table className="broker-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Contact</th>
              <th>License #</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              [...Array(3)].map((_, i) => (
                <tr key={i}>
                  <td><div className="skeleton"></div></td>
                  <td><div className="skeleton long"></div></td>
                  <td><div className="skeleton"></div></td>
                  <td><div className="skeleton short"></div></td>
                  <td><div className="skeleton short"></div></td>
                </tr>
              ))
            ) : filteredBrokers.length > 0 ? (
              filteredBrokers.map(broker => (
                <tr key={broker._id}>
                  <td>{broker.name}</td>
                  <td>{broker.email}<br /><span className="phone">{broker.phoneNumber || 'N/A'}</span></td>
                  <td>{broker.licenseNumber || 'N/A'}</td>
                  <td>
                    <span className={`status-badge status-${broker.isActive ? 'active' : 'inactive'}`}>
                      {broker.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="actions-cell">
                    <label className="switch" title={`Click to ${broker.isActive ? 'deactivate' : 'activate'}`}>
                      <input type="checkbox" checked={broker.isActive} onChange={() => handleStatusToggle(broker._id, broker.isActive)} />
                      <span className="slider round"></span>
                    </label>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="5" className="empty-state">No brokers found matching your criteria.</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <BrokerModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSave={handleAddBroker}
        isSubmitting={isSubmitting}
      />
    </div>
    
  )
}

export default AmanageBroker